import * as React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField } from '@mui/material';
import Chip from '@mui/material/Chip';
import { useState, useEffect, Fragment } from 'react';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { Text } from '../../Atoms/Text';
import { createCorrection } from '../../../Api/Correction/CorrectionApi';
import FinancialInputField from '../DetailPanel/EditModal/FinancialInputField';
import { chipStyles } from '../../../utils/helpers';
import { Icon } from '../../Atoms/Icon';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { Correction } from '../../../store/Correction';
import { RunSelection } from '../Run/RunSelection';

interface NewCorrectionDialogProps {
  open: boolean;
  onClose: (refresh: boolean) => void;
  combinedAssetId: string;
  assetNumber: string;
  supplierName: string;
  commissionsStatus: string;
  type: 'asset' | 'provisional-asset';
}

interface FormData {
  combinedAssetId: string;
  paidInRunId: number | null;
  correctsRunId: number | null;
  revenueRecurringCorrection: number | null;
  revenueOneTimeCorrection: number | null;
  note: string | null;
}

export const NewCorrectionDialog = ({
  open,
  onClose,
  combinedAssetId,
  assetNumber,
  supplierName,
  commissionsStatus,
  type
}: NewCorrectionDialogProps) => {
  const defaultFormData: FormData = {
    combinedAssetId,
    paidInRunId: null,
    correctsRunId: null,
    revenueRecurringCorrection: null,
    revenueOneTimeCorrection: null,
    note: null
  };
  const [loading, setLoading] = useState(false);
  const [dataForm, setDataForm] = useState<FormData>(defaultFormData);
  const [refreshAfterSubmit, setRefreshAfterSubmit] = useState(false);
  const { setSnack } = useSnackbar();

  const resetForm = () => {
    setDataForm(defaultFormData);
  };

  useEffect(() => {
    if (open) {
      resetForm();
      setRefreshAfterSubmit(false);
    }
  }, [open]);

  const validNote = (dataForm.note ?? '').trim() !== '';
  const formValid =
    dataForm.paidInRunId !== null &&
    ((dataForm.revenueOneTimeCorrection ?? 0) !== 0 || (dataForm.revenueRecurringCorrection ?? 0) !== 0) &&
    validNote;
  const disabled = !formValid || loading;

  const handleSave = async (close = true) => {
    if (disabled) return;
    setLoading(true);
    const result = await createCorrection(dataForm as Partial<Correction>);
    setLoading(false);
    if (result?.error) {
      setSnack({ message: result.error.message || 'Failed to create correction', type: 'error', open: true });
      return;
    }
    setSnack({ message: 'Correction created successfully', type: 'success', open: true });
    setRefreshAfterSubmit(true);
    if (close) {
      onClose(true);
    } else {
      resetForm();
    }
  };

  const handleClose = () => {
    onClose(refreshAfterSubmit);
  };

  const handleSubmitAndAddAnother = async () => {
    if (disabled) return;
    handleSave(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: (event: React.SubmitEvent) => {
            event.preventDefault();
            handleSave();
          },
          sx: { overflowY: 'visible' }
        }
      }}
      fullWidth
      maxWidth="sm"
      data-testid="create-correction-modal">
      <DialogTitle>
        <Text
          weight="bold"
          size="lg"
          color="grey9">
          New Correction
        </Text>
      </DialogTitle>
      <DialogContent sx={{ minHeight: 355, overflowY: 'visible', paddingLeft: 6, paddingRight: 6 }}>
        {loading && <ComponentSpinner label="Saving..." />}
        {!loading && (
          <Fragment>
            <div className="flex flex-row gap-x-4">
              <div className="flex flex-col gap-y-2 w-1/2">
                <Text
                  size="sm14"
                  color="grey6">
                  {type === 'asset' ? 'Asset' : 'Provisional Asset'}
                </Text>
                <div className="flex gap-1">
                  <Icon
                    className="p-2"
                    type={type === 'asset' ? 'assetMed' : 'provisionalAssetMed'}
                  />
                  {`${supplierName}: ${assetNumber}`}
                </div>
              </div>
              <div className="flex flex-col gap-y-2 w-1/2">
                <Text
                  size="sm14"
                  color="grey6">
                  Commissions Status
                </Text>
                <div className="w-content">
                  <Chip
                    label={commissionsStatus}
                    size="small"
                    sx={chipStyles(commissionsStatus)}
                    title={commissionsStatus}
                  />
                </div>
              </div>
            </div>
            <hr className="my-4" />
            <div className="flex flex-col gap-x-4">
              <Text
                size="sm14"
                color="grey6">
                Paid In
              </Text>
              <RunSelection
                selectedRunId={dataForm.paidInRunId}
                setSelectedRun={(run) => setDataForm((prevData) => ({ ...prevData, paidInRunId: run.id }))}
              />
              <Text
                size="sm14"
                color="grey6"
                className="mt-2">
                Corrects
              </Text>
              <RunSelection
                selectedRunId={dataForm.correctsRunId}
                setSelectedRun={(run) => setDataForm((prevData) => ({ ...prevData, correctsRunId: run.id }))}
              />
            </div>
            <hr className="my-4" />
            <div className="flex flex-row gap-x-4">
              <div className="flex flex-col gap-y-2 w-1/2">
                <Text
                  size="sm14"
                  color="grey6">
                  Recurring
                </Text>
                <div className="w-full">
                  <FinancialInputField
                    value={dataForm.revenueRecurringCorrection ?? ''}
                    label=""
                    name="revenueRecurringCorrection"
                    allowNegative
                    onChange={() => (e: React.ChangeEvent<HTMLInputElement>) => {
                      const { value } = e.target;
                      const parsed = value ? parseFloat(value) : NaN;
                      const revenueRecurringCorrection = isNaN(parsed) ? null : parsed;
                      setDataForm((prevData) => ({ ...prevData, revenueRecurringCorrection }));
                    }}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-y-2 w-1/2">
                <Text
                  size="sm14"
                  color="grey6">
                  One-Time
                </Text>
                <div className="w-full">
                  <FinancialInputField
                    value={dataForm.revenueOneTimeCorrection ?? ''}
                    label=""
                    name="revenueOneTimeCorrection"
                    allowNegative
                    onChange={() => (e: React.ChangeEvent<HTMLInputElement>) => {
                      const { value } = e.target;
                      const parsed = value ? parseFloat(value) : NaN;
                      const revenueOneTimeCorrection = isNaN(parsed) ? null : parsed;
                      setDataForm((prevData) => ({ ...prevData, revenueOneTimeCorrection }));
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="flex flex-row gap-x-4">
              <div className="flex flex-col gap-y-2 w-full">
                <Text
                  size="sm14"
                  color="grey6"
                  className="mt-4">
                  Note
                </Text>
                <TextField
                  sx={{ width: '100%' }}
                  value={dataForm.note || ''}
                  data-testid="note-input"
                  onChange={(e) => setDataForm((prevData) => ({ ...prevData, note: e.target.value }))}
                  size="small"
                  multiline
                  rows={3}
                />
              </div>
            </div>
          </Fragment>
        )}
      </DialogContent>
      <hr className="mb-1 mx-6" />
      {!loading && (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            data-testid="cancel-button"
            onClick={handleClose}>
            Cancel
          </Button>
          <Button
            data-testid="save-and-add-another-button"
            disabled={disabled}
            onClick={handleSubmitAndAddAnother}>
            Save and Add Another
          </Button>
          <Button
            disabled={disabled}
            type="submit"
            variant="contained">
            Save
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};
